export const IDENTITY_SESSION_KEY = "identitySession";
export const LAST_TENANT_ID_KEY = "last_tenant_id";
export const PREFERRED_TENANT_ID_KEY = "preferred_tenant_id";
export const STUDIO_SWITCH_BANNER_KEY = "studioSwitchBanner";

export interface StudioMembership {
  id?: number;
  tenant_id: number;
  tenant_name: string | null;
  roles: string[];
  status: string;
  last_accessed_at?: string | null;
  invited_at?: string | null;
  blocked: boolean;
  subscription_status?: string | null;
}

export interface IdentitySession {
  identityToken: string;
  memberships: StudioMembership[];
  expiresAt: string;
  lastTenantId?: number | null;
  /** Conta Adatis — usado no CTA “Criar meu estúdio” (Persona 4). */
  email?: string | null;
  name?: string | null;
}

export interface StudioSwitchBannerPayload {
  tenantName: string;
  expiresAt: number;
}

export const getIdentitySession = (): IdentitySession | null => {
  const raw = sessionStorage.getItem(IDENTITY_SESSION_KEY);

  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw) as IdentitySession;

    if (!parsed.identityToken || !Array.isArray(parsed.memberships)) {
      return null;
    }

    if (parsed.expiresAt && Date.now() >= new Date(parsed.expiresAt).getTime()) {
      clearIdentitySession();
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
};

export const setIdentitySession = (session: IdentitySession): void => {
  sessionStorage.setItem(IDENTITY_SESSION_KEY, JSON.stringify(session));
};

export const clearIdentitySession = (): void => {
  sessionStorage.removeItem(IDENTITY_SESSION_KEY);
};

export const getLastTenantId = (): number | null => {
  const raw = localStorage.getItem(LAST_TENANT_ID_KEY);

  if (!raw) {
    return null;
  }

  const value = Number(raw);

  return Number.isFinite(value) ? value : null;
};

export const setLastTenantId = (tenantId: number): void => {
  localStorage.setItem(LAST_TENANT_ID_KEY, String(tenantId));
};

export const getPreferredTenantId = (): number | null => {
  const raw = localStorage.getItem(PREFERRED_TENANT_ID_KEY);

  if (!raw) {
    return null;
  }

  const value = Number(raw);

  return Number.isFinite(value) ? value : null;
};

export const setPreferredTenantId = (tenantId: number): void => {
  localStorage.setItem(PREFERRED_TENANT_ID_KEY, String(tenantId));
};

export const clearPreferredTenantId = (): void => {
  localStorage.removeItem(PREFERRED_TENANT_ID_KEY);
};

export const setStudioSwitchBanner = (tenantName: string, ttlMs = 8000): void => {
  const payload: StudioSwitchBannerPayload = {
    tenantName,
    expiresAt: Date.now() + ttlMs,
  };
  sessionStorage.setItem(STUDIO_SWITCH_BANNER_KEY, JSON.stringify(payload));
};

export const getStudioSwitchBanner = (): StudioSwitchBannerPayload | null => {
  const raw = sessionStorage.getItem(STUDIO_SWITCH_BANNER_KEY);

  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw) as StudioSwitchBannerPayload;

    if (!parsed.tenantName || Date.now() >= parsed.expiresAt) {
      clearStudioSwitchBanner();
      return null;
    }

    return parsed;
  } catch {
    clearStudioSwitchBanner();
    return null;
  }
};

export const clearStudioSwitchBanner = (): void => {
  sessionStorage.removeItem(STUDIO_SWITCH_BANNER_KEY);
};

export const studioInitials = (name: string | null | undefined): string => {
  const cleaned = (name || "").trim();

  if (!cleaned) {
    return "E";
  }

  const parts = cleaned.split(/\s+/).filter(Boolean);

  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }

  return `${parts[0][0] ?? ""}${parts[1][0] ?? ""}`.toUpperCase();
};

export const isMembershipEnterable = (membership: StudioMembership): boolean =>
  membership.status === "active" && !membership.blocked;

export const isSubscriptionImpaired = (membership: StudioMembership): boolean => {
  const status = (membership.subscription_status || "").toLowerCase();

  return ["suspended", "past_due", "canceled", "cancelled", "unpaid"].includes(status);
};

export type PreferredTenantSyncResult = {
  cleared: boolean;
  tenantName: string | null;
};

/**
 * Clears preferred studio when it is no longer an enterable membership.
 */
export const syncPreferredTenantWithMemberships = (
  memberships: StudioMembership[]
): PreferredTenantSyncResult => {
  const preferred = getPreferredTenantId();

  if (preferred == null) {
    return { cleared: false, tenantName: null };
  }

  const preferredMembership = memberships.find((item) => item.tenant_id === preferred);
  const stillValid =
    preferredMembership != null && isMembershipEnterable(preferredMembership);

  if (stillValid) {
    return { cleared: false, tenantName: null };
  }

  const tenantName = preferredMembership?.tenant_name?.trim() || null;
  clearPreferredTenantId();

  return { cleared: true, tenantName };
};
